import { EventEmitter } from "events";

const createUUID = () => {
  const pattern = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
  return pattern.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const Constants = {
  CHANGE: "change",
  PRIMARY: "primary",
  SECONDARY: "secondary",
  INFO: "info",
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error"
} as const;

export type NotifyRecord = Record<string, unknown> & {
  id: string;
  type?: string;
  title?: string | null;
  message?: string | null;
  timeOut?: number;
  customClassName?: string;
  onClick?: () => void;
  priority?: boolean;
};

type NotifyInput = Record<string, unknown>;

class NotificationManager extends EventEmitter {
  listNotify: NotifyRecord[];

  constructor() {
    super();
    this.listNotify = [];
  }

  create(notify: NotifyInput) {
    const defaultNotify: NotifyRecord = {
      id: createUUID(),
      type: "info",
      title: null,
      message: null,
      timeOut: 5000,
      customClassName: ""
    };
    if (notify.priority) {
      this.listNotify.unshift(
        Object.assign(defaultNotify, notify) as NotifyRecord
      );
    } else {
      this.listNotify.push(Object.assign(defaultNotify, notify) as NotifyRecord);
    }
    this.emitChange();
  }

  primary(
    message: string | null | undefined,
    title: string | null | undefined,
    timeOut: number | undefined,
    onClick: (() => void) | undefined,
    priority: boolean | undefined,
    customClassName: string | undefined
  ) {
    this.create({
      type: Constants.PRIMARY,
      message,
      title,
      timeOut,
      onClick,
      priority,
      customClassName
    });
  }

  secondary(
    message: string | null | undefined,
    title: string | null | undefined,
    timeOut: number | undefined,
    onClick: (() => void) | undefined,
    priority: boolean | undefined,
    customClassName: string | undefined
  ) {
    this.create({
      type: Constants.SECONDARY,
      message,
      title,
      timeOut,
      onClick,
      priority,
      customClassName
    });
  }

  info(
    message: string | null | undefined,
    title: string | null | undefined,
    timeOut: number | undefined,
    onClick: (() => void) | undefined,
    priority: boolean | undefined,
    customClassName: string | undefined
  ) {
    this.create({
      type: Constants.INFO,
      message,
      title,
      timeOut,
      onClick,
      priority,
      customClassName
    });
  }

  success(
    message: string | null | undefined,
    title: string | null | undefined,
    timeOut: number | undefined,
    onClick: (() => void) | undefined,
    priority: boolean | undefined,
    customClassName: string | undefined
  ) {
    this.create({
      type: Constants.SUCCESS,
      message,
      title,
      timeOut,
      onClick,
      priority,
      customClassName
    });
  }

  warning(
    message: string | null | undefined,
    title: string | null | undefined,
    timeOut: number | undefined,
    onClick: (() => void) | undefined,
    priority: boolean | undefined,
    customClassName: string | undefined
  ) {
    this.create({
      type: Constants.WARNING,
      message,
      title,
      timeOut,
      onClick,
      priority,
      customClassName
    });
  }

  error(
    message: string | null | undefined,
    title: string | null | undefined,
    timeOut: number | undefined,
    onClick: (() => void) | undefined,
    priority: boolean | undefined,
    customClassName: string | undefined
  ) {
    this.create({
      type: Constants.ERROR,
      message,
      title,
      timeOut,
      onClick,
      priority,
      customClassName
    });
  }

  remove(notification: Pick<NotifyRecord, "id">) {
    this.listNotify = this.listNotify.filter(
      n => notification.id !== n.id
    );
    this.emitChange();
  }

  emitChange() {
    this.emit(Constants.CHANGE, this.listNotify);
  }

  addChangeListener(callback: (list: NotifyRecord[]) => void) {
    this.addListener(Constants.CHANGE, callback);
  }

  removeChangeListener(callback: (list: NotifyRecord[]) => void) {
    this.removeListener(Constants.CHANGE, callback);
  }
}

export default new NotificationManager();
